import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useUsersMe } from "@/entities/users/hooks";
import {
  useDeleteProposal,
  useEarlierProposalVersions,
  useGenerateProposal,
  useHasActiveProposalTemplate,
  useProposalRecipients,
  useProposals,
  useProposalTemplate,
  useReviseProposal,
  useWithdrawProposal
} from "./hooks";
import { ProposalsPanel } from "./ProposalsPanel";
import {
  DeleteProposalDialog,
  RegenerateProposalDialog,
  ReviseProposalDialog,
  WithdrawAndRegenerateDialog,
  WithdrawProposalDialog
} from "./ProposalActionDialogs";
import {
  isAwaitingSignature,
  isDraftOnlyActionAllowed,
  withdrawsOnRevise
} from "./draftActions";
import {
  regenerateErrorMessageKey,
  reviseErrorMessageKey,
  withdrawErrorMessageKey
} from "./reviseErrorMessage";
import {
  Proposal,
  ProposalEditorLocationState,
  ProposalRecipients,
  ProposalRow
} from "./types";
import { versionAsProposal } from "./versionAsProposal";
import { creatorRecipients, NO_RECIPIENTS } from "./recipients/recipients";
import { RecipientsModal } from "./recipients/RecipientsModal";
import { useGenerateBlockedReason } from "./termsReadiness";
import { generateErrorMessageKey } from "./generateErrorMessage";
import { useTrackingPaused } from "./tracking";

interface Props {
  dealId: string;
  contractId: string | undefined;
  // Where the editor returns to, when not the deal's Terms & Services.
  returnTo?: string;
  className?: string;
}

// Absent, not empty, while the flag is off or the account has no active
// template — the two render identically. Gates on `loading` too, since
// `useFeatureFlag` starts false and would flash the off state first.
export const ProposalsSection: React.FC<Props> = ({
  dealId,
  contractId,
  returnTo,
  className
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enabled: cpqEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlags.CRM_9761_CPQ
  );
  const flagOn = !flagLoading && cpqEnabled;

  const { data: hasActiveTemplate } = useHasActiveProposalTemplate({
    enabled: flagOn
  });
  const visible = flagOn && !!hasActiveTemplate && !!contractId;

  const {
    data: proposals,
    isLoading,
    isError: proposalsError,
    refetch: refetchProposals
  } = useProposals(dealId, {
    enabled: visible
  });
  const { data: template, isError: templateError } = useProposalTemplate({
    enabled: flagOn
  });
  const trackingPaused = useTrackingPaused();
  const { data: me, isLoading: meLoading } = useUsersMe();
  const { reason: termsBlockedReason, isLoading: termsLoading } =
    useGenerateBlockedReason(dealId, contractId);
  const generate = useGenerateProposal();
  const { mutate: remove } = useDeleteProposal(dealId);
  // Each row's call settles on its own promise; a shared mutation's callbacks
  // follow only its latest call, so a second row's Revise would strand the first.
  const { mutateAsync: revise } = useReviseProposal(dealId);
  const { mutateAsync: withdraw } = useWithdrawProposal(dealId);
  // Generate opens the recipients step at once and generates meanwhile; the
  // editor opens once both the proposal and the rep's choice are in.
  const [choosingRecipients, setChoosingRecipients] = useState(false);
  const [generated, setGenerated] = useState<Proposal>();
  const [chosenRecipients, setChosenRecipients] =
    useState<ProposalRecipients>();
  const [regenerating, setRegenerating] = useState<Proposal>();
  // A proposal still out for signature is withdrawn first, and the
  // confirmation names who it went to.
  const withdrawing = !!regenerating && isAwaitingSignature(regenerating);
  const { data: withdrawnFrom } = useProposalRecipients(
    withdrawing ? regenerating.id : undefined
  );
  const [deleting, setDeleting] = useState<Proposal>();
  // Rows whose delete is in flight: hidden until it settles, so they can't be
  // deleted twice.
  const [removingIds, setRemovingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // Each proposal, then its earlier versions that went out.
  const listed = (proposals ?? []).filter(
    (proposal) => !removingIds.has(proposal.id)
  );
  const earlierVersions = useEarlierProposalVersions(listed);
  const rows = listed.flatMap((proposal): ProposalRow[] => [
    proposal,
    ...(earlierVersions[proposal.id] ?? []).map((version) =>
      versionAsProposal(proposal, version)
    )
  ]);
  // Rows being returned to Draft or withdrawn, and one whose withdraw
  // confirmation is waiting on its recipients: their actions are held, so a
  // second click can't cut a second draft or withdraw twice.
  const [revisingIds, setRevisingIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // The row whose Revise is being confirmed; one still out for signature names
  // who it went to, so its actions are held until they load.
  const [revisingProposal, setRevisingProposal] = useState<Proposal>();
  const reviseWithdraws =
    !!revisingProposal && withdrawsOnRevise(revisingProposal);
  const { data: reviseRecipients } = useProposalRecipients(
    reviseWithdraws ? revisingProposal.id : undefined
  );
  // The row whose Withdraw is being confirmed; held until the recipients the
  // confirmation names load.
  const [withdrawingProposal, setWithdrawingProposal] = useState<Proposal>();
  const { data: withdrawRecipients } = useProposalRecipients(
    withdrawingProposal?.id
  );
  const heldIds = [
    withdrawing && !withdrawnFrom ? regenerating.id : undefined,
    reviseWithdraws && !reviseRecipients ? revisingProposal.id : undefined,
    withdrawingProposal && !withdrawRecipients
      ? withdrawingProposal.id
      : undefined
  ].filter((id): id is string => !!id);
  const busyIds =
    heldIds.length > 0 ? new Set([...revisingIds, ...heldIds]) : revisingIds;
  const doneRevising = (id: string) =>
    setRevisingIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

  const openProposal = useCallback(
    (proposal: Proposal) =>
      navigate(`/deals/${dealId}/proposals/${proposal.id}`, {
        state: returnTo ? { returnTo } : undefined
      }),
    [dealId, navigate, returnTo]
  );

  const openVersion = useCallback(
    (proposal: Proposal, versionId: string) =>
      navigate(
        `/deals/${dealId}/proposals/${proposal.id}/versions/${versionId}`
      ),
    [dealId, navigate]
  );

  // A cancelled or restarted generation never reports back: TanStack runs
  // only the latest mutate's callbacks, and reset detaches the pending one.
  const startGenerate = () => {
    setGenerated(undefined);
    setChosenRecipients(undefined);
    setChoosingRecipients(true);
    generate.mutate(dealId, {
      onSuccess: setGenerated,
      onError: (error) => {
        setChoosingRecipients(false);
        toast.error(
          t(generateErrorMessageKey(error, "contracts.proposals.generateError"))
        );
      }
    });
  };

  // Generating writes nothing, so backing out leaves nothing behind.
  const cancelGenerate = () => {
    generate.reset();
    setChoosingRecipients(false);
  };

  const continueWithRecipients = (recipients: ProposalRecipients) => {
    setChosenRecipients(recipients);
    setChoosingRecipients(false);
  };

  useEffect(() => {
    if (!generated || !chosenRecipients) return;
    const state: ProposalEditorLocationState = {
      recipients: chosenRecipients,
      returnTo
    };
    navigate(`/deals/${dealId}/proposals/${generated.id}`, { state });
  }, [generated, chosenRecipients, dealId, navigate, returnTo]);

  // Regenerating a draft writes nothing, so it happens in the editor, where
  // the rep reviews the rebuilt draft and saves it as the new version. One past
  // Draft first returns to Draft as a new version, withdrawn if still out.
  const confirmRegenerate = () => {
    if (!regenerating) return;
    const state: ProposalEditorLocationState = { regenerate: true, returnTo };
    if (isDraftOnlyActionAllowed(regenerating)) {
      navigate(`/deals/${dealId}/proposals/${regenerating.id}`, { state });
    } else {
      const { id } = regenerating;
      setRevisingIds((current) => new Set(current).add(id));
      revise({ proposalId: id })
        .then(
          (stored) =>
            navigate(`/deals/${dealId}/proposals/${stored.proposal.id}`, {
              state
            }),
          (error) => toast.error(t(regenerateErrorMessageKey(error)))
        )
        .finally(() => doneRevising(id));
    }
    setRegenerating(undefined);
  };

  // Revise opens the new draft in the editor; a refusal leaves the proposal as
  // it was, and says why.
  const confirmRevise = () => {
    if (!revisingProposal) return;
    const { id } = revisingProposal;
    setRevisingIds((current) => new Set(current).add(id));
    revise({ proposalId: id })
      .then(
        (stored) =>
          navigate(`/deals/${dealId}/proposals/${stored.proposal.id}`, {
            state: returnTo ? { returnTo } : undefined
          }),
        (error) => toast.error(t(reviseErrorMessageKey(error)))
      )
      .finally(() => doneRevising(id));
    setRevisingProposal(undefined);
  };

  // The proposal stays where it is, now Voided, so the list is read again
  // rather than anything opening; a refusal says why.
  const confirmWithdraw = () => {
    if (!withdrawingProposal) return;
    const { id } = withdrawingProposal;
    setRevisingIds((current) => new Set(current).add(id));
    withdraw({ proposalId: id })
      .then(
        () => toast.success(t("contracts.proposals.withdrawn")),
        (error) => toast.error(t(withdrawErrorMessageKey(error)))
      )
      .finally(() => doneRevising(id));
    setWithdrawingProposal(undefined);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const { id } = deleting;
    setRemovingIds((current) => new Set(current).add(id));
    remove(id, {
      onError: () => toast.error(t("contracts.proposals.deleteError")),
      onSettled: () =>
        setRemovingIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        })
    });
    setDeleting(undefined);
  };

  if (!visible) return null;

  return (
    <>
      <ProposalsPanel
        className={className}
        proposals={rows}
        isLoading={isLoading}
        // A failed refetch keeps the list it already had.
        loadError={proposalsError && !proposals}
        onRetry={() => refetchProposals()}
        trackingPaused={trackingPaused}
        busyIds={busyIds}
        onGenerate={startGenerate}
        // Held until the template says how many can sign, the contract's Terms &
        // Services are known, and the rep is, since they're given a copy.
        isGenerating={
          generate.isPending || !template || termsLoading || meLoading
        }
        termsBlockedReason={
          templateError
            ? t("contracts.proposals.templateError")
            : termsBlockedReason
        }
        termsLoading={termsLoading}
        onOpen={openProposal}
        onOpenVersion={openVersion}
        onRegenerate={setRegenerating}
        onDelete={setDeleting}
        onRevise={setRevisingProposal}
        onWithdraw={setWithdrawingProposal}
      />
      <RegenerateProposalDialog
        proposal={withdrawing ? undefined : regenerating}
        onConfirm={confirmRegenerate}
        onCancel={() => setRegenerating(undefined)}
      />
      <WithdrawAndRegenerateDialog
        // Opens once the recipients it names have loaded.
        proposal={withdrawing && withdrawnFrom ? regenerating : undefined}
        recipients={withdrawnFrom?.list ?? []}
        onConfirm={confirmRegenerate}
        onCancel={() => setRegenerating(undefined)}
      />
      <ReviseProposalDialog
        // One still out for signature opens once the recipients it names load.
        proposal={
          reviseWithdraws && !reviseRecipients ? undefined : revisingProposal
        }
        recipients={reviseRecipients?.list ?? []}
        withdraws={reviseWithdraws}
        onConfirm={confirmRevise}
        onCancel={() => setRevisingProposal(undefined)}
      />
      <WithdrawProposalDialog
        // Opens once the recipients it names have loaded.
        proposal={withdrawRecipients ? withdrawingProposal : undefined}
        recipients={withdrawRecipients?.list ?? []}
        onConfirm={confirmWithdraw}
        onCancel={() => setWithdrawingProposal(undefined)}
      />
      <DeleteProposalDialog
        proposal={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(undefined)}
      />
      <RecipientsModal
        open={choosingRecipients}
        dealId={dealId}
        recipients={me ? creatorRecipients(me) : NO_RECIPIENTS}
        signaturePlaces={template?.signaturePlaces ?? 0}
        onContinue={continueWithRecipients}
        onCancel={cancelGenerate}
      />
    </>
  );
};
